import { DesignTokens, TailwindColor } from '../types'
import {
  COLOR_VALUES,
  COLOR_VALUES_DARK,
  COLOR_VALUES_LIGHT,
  COLOR_VALUES_BG,
  NEUTRAL_BG,
  NEUTRAL_TEXT,
  NEUTRAL_TEXT_SECONDARY,
  NEUTRAL_BORDER,
} from '../data/tailwindColors'
import { RADIUS_VALUES, SHADOW_VALUES } from '../data/styleTokens'
import {
  FONT_SIZE_VALUES,
  FONT_WEIGHT_VALUES,
  getFontFamilyStack,
  getFontFamilyValue,
} from '../data/typography'

export function generateCSSVariables(tokens: DesignTokens, themeName: string): string {
  const primary = tokens.primaryColor
  const neutral = tokens.neutralColor

  const lines = [
    `/* ${themeName} - Design System Variables */`,
    `:root {`,
    `  /* Primary Colors */`,
    `  --color-primary: ${COLOR_VALUES[primary]};`,
    `  --color-primary-light: ${COLOR_VALUES_LIGHT[primary]};`,
    `  --color-primary-dark: ${COLOR_VALUES_DARK[primary]};`,
    `  --color-primary-bg: ${COLOR_VALUES_BG[primary]};`,
    ``,
  ]

  if (tokens.secondaryColor) {
    const secondary = tokens.secondaryColor as TailwindColor
    lines.push(
      `  /* Secondary Colors */`,
      `  --color-secondary: ${COLOR_VALUES[secondary]};`,
      `  --color-secondary-light: ${COLOR_VALUES_LIGHT[secondary]};`,
      `  --color-secondary-dark: ${COLOR_VALUES_DARK[secondary]};`,
      `  --color-secondary-bg: ${COLOR_VALUES_BG[secondary]};`,
      ``
    )
  }

  lines.push(
    `  /* Neutral Colors */`,
    `  --color-neutral-bg: ${NEUTRAL_BG[neutral]};`,
    `  --color-neutral-text: ${NEUTRAL_TEXT[neutral]};`,
    `  --color-neutral-text-secondary: ${NEUTRAL_TEXT_SECONDARY[neutral]};`,
    `  --color-neutral-border: ${NEUTRAL_BORDER[neutral]};`,
    ``,
    `  /* Typography */`,
    `  --font-display: ${getFontFamilyValue(tokens.displayFont)};`,
    `  --font-body: ${getFontFamilyValue(tokens.bodyFont)};`,
    `  --font-mono: ${getFontFamilyValue(tokens.monoFont)};`,
    `  --font-weight-display: ${FONT_WEIGHT_VALUES[tokens.displayWeight]};`,
    `  --font-weight-body: ${FONT_WEIGHT_VALUES[tokens.bodyWeight]};`,
    `  --font-weight-mono: ${FONT_WEIGHT_VALUES[tokens.monoWeight]};`,
    `  --font-size-display: ${FONT_SIZE_VALUES[tokens.displaySize]};`,
    `  --font-size-body: ${FONT_SIZE_VALUES[tokens.bodySize]};`,
    `  --font-size-mono: ${FONT_SIZE_VALUES[tokens.monoSize]};`,
    ``,
    `  /* Layout */`,
    `  --radius: ${RADIUS_VALUES[tokens.borderRadius]};`,
    `  --shadow: ${SHADOW_VALUES[tokens.shadow]};`,
    `}`
  )

  return lines.join('\n')
}

export function generateTailwindConfig(tokens: DesignTokens, themeName: string): string {
  const primary = tokens.primaryColor
  const neutral = tokens.neutralColor

  const colorsExtend: string[] = [
    `        primary: colors.${primary},`,
    `        neutral: colors.${neutral},`,
  ]

  if (tokens.secondaryColor) {
    colorsExtend.push(`        secondary: colors.${tokens.secondaryColor},`)
  }

  const displayFont = getFontFamilyStack(tokens.displayFont)
  const bodyFont = getFontFamilyStack(tokens.bodyFont)
  const monoFont = getFontFamilyStack(tokens.monoFont)

  return `// ${themeName} - Tailwind Configuration
import colors from 'tailwindcss/colors'

export default {
  theme: {
    extend: {
      colors: {
${colorsExtend.join('\n')}
      },
      fontFamily: {
        display: ${JSON.stringify(displayFont)},
        sans: ${JSON.stringify(bodyFont)},
        mono: ${JSON.stringify(monoFont)},
      },
      fontWeight: {
        display: '${FONT_WEIGHT_VALUES[tokens.displayWeight]}',
        body: '${FONT_WEIGHT_VALUES[tokens.bodyWeight]}',
        mono: '${FONT_WEIGHT_VALUES[tokens.monoWeight]}',
      },
      fontSize: {
        display: '${FONT_SIZE_VALUES[tokens.displaySize]}',
        body: '${FONT_SIZE_VALUES[tokens.bodySize]}',
        mono: '${FONT_SIZE_VALUES[tokens.monoSize]}',
      },
      borderRadius: {
        DEFAULT: '${RADIUS_VALUES[tokens.borderRadius]}',
      },
      boxShadow: {
        DEFAULT: '${SHADOW_VALUES[tokens.shadow]}',
      },
    },
  },
}`
}
